import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import type { CategoryDocument } from '@models/category';
import Accordion from '@ui/Accordion';
import Checkbox from '@ui/Checkbox';
import Input from '@ui/Input';
import Toggle from '@ui/Toggle';
import { getGenderLabel } from '@util/createListingHelpers';
import type { PageSideFilters } from '@util/hooks/useProductsPage';
import {
  ASPECT_RATIOS,
  cn,
  CONSTRUCTIONS,
  getCountryLabelFromCode,
  isMobile,
  MOUNTING_POSITIONS,
  RIM_SIZES,
  TIRE_WIDTHS,
} from '@util/index';
import { useAuth } from 'context/AuthContext';
import { CheckIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import {
  isVehicleCategory,
  VEHICLE_CATEGORIES,
  type FacetCounts,
} from 'models/product';
import type { Bike } from 'models/shared';
import {
  BOOT_SIZES,
  type Gender,
  LETTER_SIZES,
  PANT_SIZES,
  rideTypeChoices,
} from 'models/shared';
import type { UserSizes } from 'models/user';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import AccordionFilterSection from './components/AccordionFilterSection';
import CheckboxFilterSection from './components/CheckboxFilterSection';
import MySizesGarage from './components/MySizesGarage';
import PriceRangeFilterSection from './components/PriceRangeFilterSection';
import { useQuery } from '@tanstack/react-query';
import { getBrands } from '@util/firestore/brands/brands.service';
import { MXLockerLogoSmall } from '@c/icons/MXLockerLogoSmall';
export interface FilterItem {
  key: string;
  label: string;
  selected?: boolean;
}

interface FilterProps {
  categories?: CategoryDocument[];
  facet_counts?: FacetCounts;
  mostly_parts?: boolean;
  filterState: PageSideFilters;
  pageType?: string;
  handleFilterChange: (key: string, value: any) => void;
  handlePriceRangeChange: (min: number | null, max: number | null) => void;
  handleCategoryChange: (
    category: string,
    level: 'ride_type' | 'category' | 'category1' | 'category2' | 'category3'
  ) => void;
  handleBikeSelection: ({
    bike,
    bikes,
  }: {
    bike?: Bike | null;
    bikes?: Bike[];
  }) => void;
  handleSizeChange: (sizes: UserSizes) => void;
  activePage:
    | 'main'
    | 'categories'
    | 'brands'
    | 'price'
    | 'shipsTo'
    | 'shipsFrom'
    | 'bootSizes'
    | 'pantSizes'
    | 'letterSizes'
    | 'rideTypes';
  setActivePage: (
    page:
      | 'main'
      | 'categories'
      | 'brands'
      | 'price'
      | 'shipsTo'
      | 'shipsFrom'
      | 'bootSizes'
      | 'pantSizes'
      | 'letterSizes'
      | 'rideTypes'
  ) => void;
  dismissFilterDrawer?: () => void;
  pendingFilterChanges?: Partial<PageSideFilters>;
  setPendingFilterChanges?: (
    pendingFilterChanges: Partial<PageSideFilters>
  ) => void;
}

const Filters = ({
  categories,
  filterState,
  handlePriceRangeChange,
  handleFilterChange,
  handleCategoryChange,
  handleBikeSelection,
  handleSizeChange,
  mostly_parts,
  facet_counts,
  pageType,
  activePage,
  setActivePage,
  pendingFilterChanges,
  setPendingFilterChanges,
}: FilterProps) => {
  const { userDoc } = useAuth();
  const params = useSearchParams();
  const filtersPathname = usePathname();
  const showMXDirectFilter =
    pageType !== 'profile' &&
    pageType !== 'onsale' &&
    !filtersPathname?.includes('-for-sale') &&
    !isVehicleCategory(filterState.product_cats?.category) &&
    !VEHICLE_CATEGORIES.some((c) => filterState.category?.includes(c));
  const [brandFilter, setBrandFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');

  const { data: allBrands } = useQuery({
    queryKey: ['allBrands'],
    queryFn: getBrands,
  });
  const brands = brandFilter
    ? allBrands
        ?.filter((b) =>
          b.name.toLowerCase().includes(brandFilter.toLowerCase())
        )
        .map((b) => ({
          key: b.url,
          label: b.name,
          count: 0,
        })) ?? []
    : facet_counts?.brand?.map((b) => ({
        key: b.value,
        label: b.value,
        count: b.count,
      })) ?? [];

  // Tire specs are only carried by MX Direct listings, so filtering by one
  // hides every other tire. They are offered once the shopper is on the Tires
  // shelf — a category page that is mostly tires, or a search that took the
  // Tires category — and not to a search that merely turned up mostly tires.
  const tire_count =
    facet_counts?.category2?.find((c) => c.value === 'Tires')?.count ?? 0;
  const mostly_tires =
    filterState.product_cats?.category2 === 'Tires' ||
    (pageType === 'category' &&
      tire_count > 0 &&
      tire_count >
        (facet_counts?.category2?.reduce((acc, c) => acc + c.count, 0) ?? 0) *
          0.5);

  const pant_count =
    facet_counts?.category2?.find((c) => c.value === 'Pants')?.count ?? 0;
  const mostly_pants =
    pant_count > 0 &&
    pant_count >
      (facet_counts?.category2?.reduce((acc, c) => acc + c.count, 0) ?? 0) *
        0.5;

  const boot_count =
    facet_counts?.category1?.find((c) => c.value === 'Boots')?.count ?? 0;
  const mostly_boots =
    boot_count > 0 &&
    boot_count >
      (facet_counts?.category1?.reduce((acc, c) => acc + c.count, 0) ?? 0) *
        0.5;

  const helmet_count =
    facet_counts?.category1?.find((c) => c.value === 'Helmets')?.count ?? 0;
  const mostly_helmets =
    helmet_count > 0 &&
    helmet_count >
      (facet_counts?.category1?.reduce((acc, c) => acc + c.count, 0) ?? 0) *
        0.5;
  function formatFacetCounts(): {
    hierarchy: {
      mainCats: {
        value: string;
        count: number;
        order: string;
      }[];
      otherCats: {
        value: string;
        count: number;
        order: string;
      }[];
    };
  } {
    const cat1s =
      facet_counts?.category1?.map((cat) => ({
        value: cat.value,
        count: cat.count,
        highlighted: cat.highlighted,
        order: 'category1',
      })) ?? [];
    const cat2s =
      facet_counts?.category2?.map((cat) => ({
        value: cat.value,
        count: cat.count,
        highlighted: cat.highlighted,
        order: 'category2',
      })) ?? [];
    const cat3s =
      facet_counts?.category3?.map((cat) => ({
        value: cat.value,
        count: cat.count,
        highlighted: cat.highlighted,
        order: 'category3',
      })) ?? [];

    const hierarchy = {
      mainCats:
        facet_counts?.category?.map((cat) => ({
          value: cat.value,
          count: cat.count,
          highlighted: cat.highlighted,
          order: 'category',
        })) ?? [],
      otherCats: [...cat1s, ...cat2s, ...cat3s]
        .sort((a, b) => b.count - a.count)
        .filter(
          (c, i, self) => i === self.findIndex((t) => t.value === c.value)
        ),
    };
    return { hierarchy };
  }
  const formattedFacetCounts = formatFacetCounts();

  const countryOptions = ['US', 'CA', 'MX', 'PR', 'EU', 'AU', 'SAM'];

  const toggleOEMFilter = () => {
    const newArr = [...(filterState.brands ?? [])];
    if (!newArr.includes('OEM')) {
      newArr.push('OEM');
    } else {
      newArr.splice(newArr.indexOf('OEM'), 1); //deleting
    }
    handleFilterChange('brands', newArr);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  if (isMobile() && activePage === 'brands') {
    const selectedBrands = filterState.brands ?? [];

    return (
      <div className="flex flex-col space-y-16 pb-8">
        <div className="flex w-full items-center gap-4 rounded-[2rem] bg-[#F1F1F1] px-8 py-4">
          <MagnifyingGlassIcon className="h-8 w-8 text-zinc-700" />

          <input
            type="text"
            className="h-full w-full border-none bg-transparent text-zinc-800 outline-none placeholder:text-zinc-500"
            placeholder="Search brand name"
            value={brandFilter}
            onChange={(e) => {
              setBrandFilter(e.target.value);
            }}
          />
        </div>

        {selectedBrands.length > 0 && (
          <div className="flex flex-wrap gap-3 px-2">
            {selectedBrands.map((brandKey) => (
              <div
                key={brandKey}
                className="flex items-center gap-4 rounded-xl bg-[#F1F1F1] px-6 py-3"
              >
                <span className="text-[1.6rem] font-medium text-zinc-800">
                  {brandKey}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const newBrands = selectedBrands.filter(
                      (b) => b !== brandKey
                    );
                    handleFilterChange('brands', newBrands);
                  }}
                  className="flex h-6 w-6 items-center justify-center"
                  aria-label={`Remove ${brandKey}`}
                >
                  <span className="text-[2rem">
                    <XIcon className="h-6 w-6" />
                  </span>
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col space-y-4">
          {brands.map((brand) => {
            const isSelected = selectedBrands.includes(brand.key);

            return (
              <button
                type="button"
                onClick={() => {
                  const newBrands = isSelected
                    ? selectedBrands.filter((b) => b !== brand.key)
                    : [...selectedBrands, brand.key];
                  handleFilterChange('brands', newBrands);
                }}
                key={brand.key}
                className={cn(
                  'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
                  isSelected && 'bg-zinc-100'
                )}
              >
                <div className="flex items-center gap-2">
                  {brand.label}

                  {!!brand.count && (
                    <span className="text-[1.6rem] font-medium text-zinc-500">
                      ({brand.count})
                    </span>
                  )}
                </div>

                {isSelected && (
                  <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                    <CheckIcon className="h-full w-full text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (isMobile() && activePage === 'bootSizes') {
    const selectedSizes = filterState.numberSizes ?? [];
    const bootSizeOptions =
      BOOT_SIZES[
        (filterState.genders?.length === 1
          ? filterState.genders[0]
          : 'U') as Gender
      ];

    return (
      <div className="flex flex-col space-y-16 pb-8">
        {selectedSizes.length > 0 && (
          <div className="flex flex-wrap gap-3 px-2">
            {selectedSizes.map((size) => (
              <div
                key={size}
                className="flex items-center gap-4 rounded-xl bg-[#F1F1F1] px-6 py-3"
              >
                <span className="text-[1.6rem] font-medium text-zinc-800">
                  {size}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const newSizes = selectedSizes.filter((s) => s !== size);
                    handleFilterChange('numberSizes', newSizes);
                  }}
                  className="flex h-6 w-6 items-center justify-center"
                  aria-label={`Remove ${size}`}
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col space-y-4">
          {bootSizeOptions.map((size) => {
            const sizeStr = size.toString();
            const isSelected = selectedSizes.includes(sizeStr);

            return (
              <button
                type="button"
                onClick={() => {
                  const newSizes = isSelected
                    ? selectedSizes.filter((s) => s !== sizeStr)
                    : [...selectedSizes, sizeStr];
                  handleFilterChange('numberSizes', newSizes);
                }}
                key={sizeStr}
                className={cn(
                  'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
                  isSelected && 'bg-zinc-100'
                )}
              >
                <div className="flex items-center gap-2">{size}</div>

                {isSelected && (
                  <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                    <CheckIcon className="h-full w-full text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (isMobile() && activePage === 'pantSizes') {
    const selectedSizes = filterState.numberSizes ?? [];
    const pantSizeOptions =
      PANT_SIZES[
        (filterState.genders?.length === 1
          ? filterState.genders[0]
          : 'U') as Gender
      ];

    return (
      <div className="flex flex-col space-y-16 pb-8">
        {selectedSizes.length > 0 && (
          <div className="flex flex-wrap gap-3 px-2">
            {selectedSizes.map((size) => (
              <div
                key={size}
                className="flex items-center gap-4 rounded-xl bg-[#F1F1F1] px-6 py-3"
              >
                <span className="text-[1.6rem] font-medium text-zinc-800">
                  {size}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const newSizes = selectedSizes.filter((s) => s !== size);
                    handleFilterChange('numberSizes', newSizes);
                  }}
                  className="flex h-6 w-6 items-center justify-center"
                  aria-label={`Remove ${size}`}
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col space-y-4">
          {pantSizeOptions.map((size) => {
            const sizeStr = size.toString();
            const isSelected = selectedSizes.includes(sizeStr);

            return (
              <button
                type="button"
                onClick={() => {
                  const newSizes = isSelected
                    ? selectedSizes.filter((s) => s !== sizeStr)
                    : [...selectedSizes, sizeStr];
                  handleFilterChange('numberSizes', newSizes);
                }}
                key={sizeStr}
                className={cn(
                  'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
                  isSelected && 'bg-zinc-100'
                )}
              >
                <div className="flex items-center gap-2">{size}</div>

                {isSelected && (
                  <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                    <CheckIcon className="h-full w-full text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (isMobile() && activePage === 'letterSizes') {
    const selectedSizes = filterState.letterSizes ?? [];

    return (
      <div className="flex flex-col space-y-16 pb-8">
        {selectedSizes.length > 0 && (
          <div className="flex flex-wrap gap-3 px-2">
            {selectedSizes.map((size) => (
              <div
                key={size}
                className="flex items-center gap-4 rounded-xl bg-[#F1F1F1] px-6 py-3"
              >
                <span className="text-[1.6rem] font-medium text-zinc-800">
                  {size}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const newSizes = selectedSizes.filter((s) => s !== size);
                    handleFilterChange('letterSizes', newSizes);
                  }}
                  className="flex h-6 w-6 items-center justify-center"
                  aria-label={`Remove ${size}`}
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col space-y-4">
          {LETTER_SIZES.map((size) => {
            const sizeStr = size.toString();
            const isSelected = selectedSizes.includes(sizeStr);

            return (
              <button
                type="button"
                onClick={() => {
                  const newSizes = isSelected
                    ? selectedSizes.filter((s) => s !== sizeStr)
                    : [...selectedSizes, sizeStr];
                  handleFilterChange('letterSizes', newSizes);
                }}
                key={sizeStr}
                className={cn(
                  'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
                  isSelected && 'bg-zinc-100'
                )}
              >
                <div className="flex items-center gap-2">{size}</div>

                {isSelected && (
                  <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                    <CheckIcon className="h-full w-full text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (isMobile() && activePage === 'rideTypes') {
    const selectedRideTypes = filterState.rideTypes ?? [];

    return (
      <div className="flex flex-col space-y-16 pb-8">
        {selectedRideTypes.length > 0 && (
          <div className="flex flex-wrap gap-3 px-2">
            {selectedRideTypes.map((rideType) => (
              <div
                key={rideType}
                className="flex items-center gap-4 rounded-xl bg-[#F1F1F1] px-6 py-3"
              >
                <span className="text-[1.6rem] font-medium text-zinc-800">
                  {rideType}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const newRideTypes = selectedRideTypes.filter(
                      (rt) => rt !== rideType
                    );
                    handleFilterChange('rideTypes', newRideTypes);
                  }}
                  className="flex h-6 w-6 items-center justify-center"
                  aria-label={`Remove ${rideType}`}
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col space-y-4">
          {rideTypeChoices.map((rideType) => {
            const isSelected = selectedRideTypes.includes(rideType);
            const count = facet_counts?.ride_types?.find(
              (rt) => rt.value === rideType
            )?.count;

            return (
              <button
                type="button"
                onClick={() => {
                  const newRideTypes = isSelected
                    ? selectedRideTypes.filter((rt) => rt !== rideType)
                    : [...selectedRideTypes, rideType];
                  handleFilterChange('rideTypes', newRideTypes);
                }}
                key={rideType}
                className={cn(
                  'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
                  isSelected && 'bg-zinc-100'
                )}
              >
                <div className="flex items-center gap-2">
                  {rideType}
                  {count !== undefined && (
                    <span className="text-[1.6rem] font-medium text-zinc-500">
                      ({count})
                    </span>
                  )}
                </div>

                {isSelected && (
                  <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                    <CheckIcon className="h-full w-full text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (isMobile() && activePage === 'categories') {
    if (pageType === 'category' && categories?.length) {
      return (
        <div className="flex flex-col space-y-16 pb-8">
          <div className="flex flex-col space-y-4">
            {categories.map((category) => {
              return (
                <Link
                  key={category.id}
                  href={{
                    pathname: category.full_slug,
                    query: {
                      ...Object.fromEntries(params?.entries() ?? []),
                    },
                  }}
                  className="flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium transition-all duration-150 ease-linear hover:font-semibold hover:text-brand-secondary sm:py-2 sm:text-[1.5rem]"
                >
                  <div className="flex items-center gap-2">{category.name}</div>
                </Link>
              );
            })}
          </div>
        </div>
      );
    }

    const selectedCategories = pendingFilterChanges?.product_cats ?? {};
    const getSelectedCategory = () => {
      if (selectedCategories.category) return selectedCategories.category;
      if (selectedCategories.category1) return selectedCategories.category1;
      if (selectedCategories.category2) return selectedCategories.category2;
      if (selectedCategories.category3) return selectedCategories.category3;
      return null;
    };

    const onDeselectCategory = () => {
      const newProductCats = {
        ...pendingFilterChanges?.product_cats,
      };

      // Find which category key contains the selected category value and set it to undefined
      if (newProductCats.category === selectedCategory) {
        newProductCats.category = undefined;
      }
      if (newProductCats.category1 === selectedCategory) {
        newProductCats.category1 = undefined;
      }
      if (newProductCats.category2 === selectedCategory) {
        newProductCats.category2 = undefined;
      }
      if (newProductCats.category3 === selectedCategory) {
        newProductCats.category3 = undefined;
      }

      setPendingFilterChanges?.({
        ...pendingFilterChanges,
        product_cats: newProductCats,
      });
    };

    const selectedCategory = getSelectedCategory();

    return (
      <div className="flex flex-col space-y-16 pb-8">
        {selectedCategory && (
          <div className="flex flex-wrap gap-3 px-2">
            {selectedCategory && (
              <div className="flex items-center gap-4 rounded-xl bg-[#F1F1F1] px-6 py-3">
                <span className="text-[1.6rem] font-medium text-zinc-800">
                  {selectedCategory}
                </span>
                <button
                  type="button"
                  onClick={onDeselectCategory}
                  className="flex h-6 w-6 items-center justify-center"
                  aria-label={`Remove ${selectedCategory}`}
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col space-y-4">
          {formattedFacetCounts.hierarchy.otherCats?.map((category) => {
            const isSelected =
              (category.order === 'category' &&
                pendingFilterChanges?.product_cats?.category ===
                  category.value) ||
              (category.order === 'category1' &&
                pendingFilterChanges?.product_cats?.category1 ===
                  category.value) ||
              (category.order === 'category2' &&
                pendingFilterChanges?.product_cats?.category2 ===
                  category.value) ||
              (category.order === 'category3' &&
                pendingFilterChanges?.product_cats?.category3 ===
                  category.value);

            return (
              <button
                type="button"
                key={category.value}
                onClick={() => {
                  if (isSelected) {
                    onDeselectCategory();
                  } else {
                    handleCategoryChange(
                      category.value,
                      category.order as
                        | 'ride_type'
                        | 'category'
                        | 'category1'
                        | 'category2'
                        | 'category3'
                    );
                  }
                }}
                className={cn(
                  'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
                  isSelected && 'bg-zinc-100'
                )}
              >
                <div className="flex items-center gap-2">
                  {category.value}
                  <span className="text-[1.6rem] font-medium text-zinc-500">
                    ({category.count})
                  </span>
                </div>

                {isSelected && (
                  <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                    <CheckIcon className="h-full w-full text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (isMobile() && activePage === 'price') {
    const isUnder200Active =
      (filterState.minPrice === 0 ||
        filterState.minPrice === null ||
        filterState.minPrice === undefined) &&
      filterState.maxPrice === 200;
    const is200To500Active =
      filterState.minPrice === 200 && filterState.maxPrice === 500;
    const isOver500Active =
      filterState.minPrice === 501 &&
      (filterState.maxPrice === null || filterState.maxPrice === undefined);

    return (
      <div className="flex flex-col space-y-16 pb-8">
        <div className="flex flex-col space-y-4">
          <button
            type="button"
            className={cn(
              'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
              isUnder200Active && 'bg-zinc-100'
            )}
            onClick={() => {
              handlePriceRangeChange(0, 200);
            }}
          >
            <span>Under $200</span>
            {isUnder200Active && (
              <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                <CheckIcon className="h-full w-full text-white" />
              </div>
            )}
          </button>
          <button
            type="button"
            className={cn(
              'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
              is200To500Active && 'bg-zinc-100'
            )}
            onClick={() => {
              handlePriceRangeChange(200, 500);
            }}
          >
            <span>$200 - $500</span>
            {is200To500Active && (
              <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                <CheckIcon className="h-full w-full text-white" />
              </div>
            )}
          </button>
          <button
            type="button"
            className={cn(
              'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
              isOver500Active && 'bg-zinc-100'
            )}
            onClick={() => {
              handlePriceRangeChange(501, null);
            }}
          >
            <span>Over $500</span>
            {isOver500Active && (
              <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                <CheckIcon className="h-full w-full text-white" />
              </div>
            )}
          </button>
        </div>

        <div className="flex w-full items-center justify-between gap-12">
          <div className="flex w-full items-center justify-between rounded-2xl bg-[#F1F1F1] p-2">
            <input
              type="number"
              placeholder="Min"
              value={filterState.minPrice ?? ''}
              onChange={(e) => {
                const value = e.target.value ? Number(e.target.value) : null;
                handlePriceRangeChange(value, filterState.maxPrice);
              }}
              className="w-full border-none bg-transparent outline-none"
            />

            <span className="text-[1.6rem] font-semibold text-zinc-700">
              USD
            </span>
          </div>

          <span className="text-[1.8rem] font-medium text-zinc-700">to</span>

          <div className="flex w-full items-center justify-between rounded-2xl bg-[#F1F1F1] p-2">
            <input
              type="number"
              placeholder="Max"
              value={filterState.maxPrice ?? ''}
              onChange={(e) => {
                const value = e.target.value ? Number(e.target.value) : null;
                handlePriceRangeChange(filterState.minPrice, value);
              }}
              className="w-full border-none bg-transparent outline-none"
            />

            <span className="text-[1.6rem] font-semibold text-zinc-700">
              USD
            </span>
          </div>
        </div>

        {(filterState.minPrice !== null || filterState.maxPrice !== null) && (
          <div className="flex w-full justify-center">
            <button
              type="button"
              className="text-[1.8rem] font-semibold text-brand-secondary"
              onClick={() => {
                handlePriceRangeChange(null, null);
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    );
  }

  if (isMobile() && activePage === 'shipsTo') {
    const countries = countryFilter
      ? countryOptions
          .filter((code) =>
            getCountryLabelFromCode(code)
              .toLowerCase()
              .includes(countryFilter.toLowerCase())
          )
          .map((code) => ({
            key: code,
            label: getCountryLabelFromCode(code),
          }))
      : countryOptions.map((code) => ({
          key: code,
          label: getCountryLabelFromCode(code),
        }));

    return (
      <div className="flex flex-col space-y-16 pb-8">
        <div className="flex w-full items-center gap-4 rounded-[2rem] bg-[#F1F1F1] px-8 py-4">
          <MagnifyingGlassIcon className="h-8 w-8 text-zinc-700" />

          <input
            type="text"
            className="h-full w-full border-none bg-transparent text-zinc-800 outline-none placeholder:text-zinc-500"
            placeholder="Search country"
            value={countryFilter}
            onChange={(e) => {
              setCountryFilter(e.target.value);
            }}
          />
        </div>

        <div className="flex flex-col space-y-4">
          {countries.map((country) => {
            const isSelected =
              filterState.shipTo?.includes(country.key) ?? false;

            return (
              <button
                type="button"
                onClick={() => {
                  const newArr = [...(filterState.shipTo ?? [])];
                  if (!newArr.includes(country.key)) {
                    newArr.push(country.key);
                  } else {
                    newArr.splice(newArr.indexOf(country.key), 1);
                  }
                  handleFilterChange('shipTo', newArr);
                }}
                key={country.key}
                className={cn(
                  'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
                  isSelected && 'bg-zinc-100'
                )}
              >
                <div className="flex items-center gap-2">{country.label}</div>

                {isSelected && (
                  <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                    <CheckIcon className="h-full w-full text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (isMobile() && activePage === 'shipsFrom') {
    const countries = countryFilter
      ? countryOptions
          .filter((code) =>
            getCountryLabelFromCode(code)
              .toLowerCase()
              .includes(countryFilter.toLowerCase())
          )
          .map((code) => ({
            key: code,
            label: getCountryLabelFromCode(code),
          }))
      : countryOptions.map((code) => ({
          key: code,
          label: getCountryLabelFromCode(code),
        }));

    return (
      <div className="flex flex-col space-y-16 pb-8">
        <div className="flex w-full items-center gap-4 rounded-[2rem] bg-[#F1F1F1] px-8 py-4">
          <MagnifyingGlassIcon className="h-8 w-8 text-zinc-700" />

          <input
            type="text"
            className="h-full w-full border-none bg-transparent text-zinc-800 outline-none placeholder:text-zinc-500"
            placeholder="Search country"
            value={countryFilter}
            onChange={(e) => {
              setCountryFilter(e.target.value);
            }}
          />
        </div>

        <div className="flex flex-col space-y-4">
          {countries.map((country) => {
            const isSelected =
              filterState.shipFrom?.includes(country.key) ?? false;

            return (
              <button
                type="button"
                onClick={() => {
                  const newArr = [...(filterState.shipFrom ?? [])];
                  if (!newArr.includes(country.key)) {
                    newArr.push(country.key);
                  } else {
                    newArr.splice(newArr.indexOf(country.key), 1);
                  }
                  handleFilterChange('shipFrom', newArr);
                }}
                key={country.key}
                className={cn(
                  'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
                  isSelected && 'bg-zinc-100'
                )}
              >
                <div className="flex items-center gap-2">{country.label}</div>

                {isSelected && (
                  <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                    <CheckIcon className="h-full w-full text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col rounded-brand bg-inherit pb-32 sm:pb-0">
      {showMXDirectFilter && (
        <div className="mb-6 hidden flex-col gap-3 bg-[#F6F6F6] p-6 sm:flex">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="size-12 rounded-xl bg-brand-secondary p-1">
                <MXLockerLogoSmall fill="white" className="size-full" />
              </div>
              <span className="text-[1.6rem] font-semibold text-black">
                MXLocker Direct
              </span>
            </div>
            <Toggle
              enabled={filterState.mxDirect === true}
              onChange={(enabled) => handleFilterChange('mxDirect', enabled)}
            />
          </div>
          <p className="text-[1.5rem] leading-snug text-[#707070]">
            Products sold directly from the MX Locker warehouse. Typically
            arrives in 2-3 business days.
          </p>
        </div>
      )}
      {userDoc?.country?.code && userDoc.country.code !== 'US' && (
        <div className="my-2 flex w-full justify-between rounded-2xl border-2 border-brand-secondary bg-brand-secondary-light px-3 py-6 sm:py-2">
          <span className="text-[1.5rem] font-semibold">
            {`Ship to My Country (${userDoc?.country.code})`}
          </span>
          <Toggle
            enabled={
              filterState.shipTo?.includes(userDoc?.country.code) ?? false
            }
            onChange={() => {
              const newArr = [...(filterState.shipTo ?? [])];
              if (!newArr.includes(userDoc?.country.code)) {
                newArr.push(userDoc?.country.code);
              } else {
                newArr.splice(newArr.indexOf(userDoc?.country.code), 1); //deleting
              }
              handleFilterChange('shipTo', newArr);
            }}
          />
        </div>
      )}
      <div className="mb-8 hidden sm:block">
        <MySizesGarage
          userDoc={userDoc}
          facet_counts={facet_counts}
          filterState={filterState}
          handleBikeSelection={handleBikeSelection}
          handleSizeSelection={handleSizeChange}
        />
      </div>

      <div className="mb-8 block sm:hidden">
        <div className="flex flex-col items-start gap-4 pl-2">
          <span className="text-[1.8rem] font-semibold text-black">
            Condition
          </span>

          <div className="flex items-center gap-4">
            <button
              type="button"
              className={cn(
                'flex shrink-0 items-center justify-center rounded-xl px-4 py-2 text-[1.8rem] font-medium',
                filterState.conditions?.includes('New')
                  ? 'bg-black text-white'
                  : 'bg-[#F1F1F1] text-black'
              )}
              onClick={() => {
                handleFilterChange('conditions', ['New']);
              }}
            >
              New
            </button>

            <button
              type="button"
              className={cn(
                'flex shrink-0 items-center justify-center rounded-xl px-4 py-2 text-[1.8rem] font-medium',
                filterState.conditions?.includes('Used')
                  ? 'bg-black text-white'
                  : 'bg-[#F1F1F1] text-black'
              )}
              onClick={() => {
                handleFilterChange('conditions', ['Used']);
              }}
            >
              Used
            </button>
          </div>
        </div>
      </div>

      {!isMobile() && pageType !== 'category' && (
        <AccordionFilterSection
          title="Categories"
          startOpen={pageType === 'search'}
        >
          <div className="flex max-h-[45rem] flex-col gap-4 overflow-y-auto overflow-x-hidden px-[1rem] pb-[2rem]">
            {formattedFacetCounts.hierarchy.otherCats.map((cat) => {
              return <FacetCategoryLink cat={cat} key={cat.value} />;
            })}
          </div>
        </AccordionFilterSection>
      )}

      {!isMobile() && pageType === 'category' && !!categories?.length && (
        <div className="border-b border-zinc-300">
          <Accordion
            title={'Categories'}
            titleClassName="text-[2.1rem] sm:text-[1.8rem] font-semibold"
            startOpen={true}
          >
            <div className="flex max-h-[45rem] flex-col gap-4 overflow-y-auto overflow-x-hidden px-[1rem] pb-[2rem]">
              {categories.map((cat) => {
                return <CategoryLink cat={cat} key={cat.id} />;
              })}
            </div>
          </Accordion>
        </div>
      )}

      {isMobile() && pageType === 'category' && !!categories?.length && (
        <button
          type="button"
          onClick={() => setActivePage('categories')}
          className="flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
        >
          <span className="text-[1.8rem] font-semibold text-black">
            Categories
          </span>

          <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
        </button>
      )}

      {showMXDirectFilter && (
        <div className="flex w-full items-center justify-between border-b border-zinc-300 px-3 py-10 sm:hidden">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-lg bg-brand-secondary p-1">
              <MXLockerLogoSmall fill="white" className="size-full" />
            </div>
            <span
              role="heading"
              aria-level={2}
              className="text-[1.8rem] font-semibold"
            >
              MXLocker Direct
            </span>
          </div>

          <Toggle
            enabled={filterState.mxDirect === true}
            onChange={(enabled) => handleFilterChange('mxDirect', enabled)}
          />
        </div>
      )}

      {mostly_parts && (
        <div
          className="flex w-full justify-between border-b border-zinc-300 px-3 py-10"
          // the below is not WCAG compliant - overlapping buttons
          // onClick={() => {
          //   // if we're on mobile, toggle the switch when the user clicks anywhere on the filter section
          //   if (isMobile()) {
          //     toggleOEMFilter();
          //   }
          // }}
          // role="button"
          // tabIndex={0}
        >
          <span
            role="heading"
            aria-level={2}
            className="text-[1.8rem] font-semibold"
          >
            OEM
          </span>

          <Toggle
            enabled={!!filterState.brands?.includes('OEM')}
            onChange={() => {
              toggleOEMFilter();

              window.scrollTo({
                top: 0,
                behavior: 'smooth',
              });
            }}
          />
        </div>
      )}

      {isMobile() && pageType !== 'category' && (
        <button
          type="button"
          onClick={() => setActivePage('rideTypes')}
          className="flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
        >
          <span className="text-[1.8rem] font-semibold text-black">
            Ride Types{' '}
          </span>

          <div className="flex items-center gap-2">
            {!!filterState.rideTypes?.length && (
              <span className="w-fit max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-400">
                {filterState.rideTypes.join(', ')}
              </span>
            )}

            <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
          </div>
        </button>
      )}

      {!isMobile() && pageType !== 'category' && (
        <CheckboxFilterSection
          title="Ride Types"
          filterOptions={rideTypeChoices.map((rt) => ({
            key: rt,
            label: rt,
          }))}
          startOpen
          onChange={(values) => handleFilterChange('rideTypes', values)}
          filterState={filterState.rideTypes ?? []}
          facet_counts={facet_counts?.ride_types}
          showSelectedFirst
        />
      )}

      {isMobile() && pageType !== 'category' && (
        <button
          type="button"
          onClick={() => setActivePage('categories')}
          className="flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
        >
          <span className="text-[1.8rem] font-semibold text-black">
            Categories{' '}
            {filterState.category && (
              <span className="text-zinc-600">({filterState.category[0]})</span>
            )}
          </span>

          <div className="flex items-center gap-2">
            {!!filterState.product_cats && (
              <span className="w-fit max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-400">
                {Object.values(filterState.product_cats)
                  .filter(Boolean)
                  .join(', ')}
              </span>
            )}

            <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
          </div>
        </button>
      )}

      {isMobile() && (!!brands?.length || !!brandFilter.length) && (
        <button
          type="button"
          onClick={() => setActivePage('brands')}
          className="mt-4 flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
        >
          <span className="text-[1.8rem] font-semibold text-black">
            Brands{' '}
          </span>

          <div className="flex items-center gap-2">
            {!!filterState.brands?.length && (
              <span className="w-fit max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-400">
                {filterState.brands.join(', ')}
              </span>
            )}

            <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
          </div>
        </button>
      )}
      {isMobile() && !mostly_parts && !mostly_boots && (
        <button
          type="button"
          onClick={() => setActivePage('letterSizes')}
          className="flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
        >
          <span className="text-[1.8rem] font-semibold text-black">
            {mostly_helmets ? 'Helmet Size' : 'Size'}{' '}
          </span>

          <div className="flex items-center gap-2">
            {!!filterState.letterSizes?.length && (
              <span className="w-fit max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-400">
                {filterState.letterSizes.join(', ')}
              </span>
            )}

            <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
          </div>
        </button>
      )}
      {!isMobile() && !mostly_parts && !mostly_boots && (
        <CheckboxFilterSection
          key={mostly_helmets ? 'helmet-size' : 'letter-size'}
          title={mostly_helmets ? 'Helmet Size' : 'Size'}
          filterOptions={LETTER_SIZES.map((g) => ({
            key: g + '',
            label: g + '',
          }))}
          onChange={(values) => handleFilterChange('letterSizes', values)}
          startOpen={mostly_helmets || !!filterState.letterSizes?.length}
          filterState={filterState.letterSizes ?? []}
        />
      )}
      {isMobile() && mostly_boots && (
        <button
          type="button"
          onClick={() => setActivePage('bootSizes')}
          className="flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
        >
          <span className="text-[1.8rem] font-semibold text-black">
            Boot Size{' '}
          </span>

          <div className="flex items-center gap-2">
            {!!filterState.numberSizes?.length && (
              <span className="w-fit max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-400">
                {filterState.numberSizes.join(', ')}
              </span>
            )}

            <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
          </div>
        </button>
      )}

      {!isMobile() && mostly_boots && (
        <CheckboxFilterSection
          title="Boot Size"
          filterOptions={BOOT_SIZES[
            (filterState.genders?.length === 1
              ? filterState.genders[0]
              : 'U') as Gender
          ].map((g) => ({ key: g + '', label: g + '' }))}
          onChange={(values) => handleFilterChange('numberSizes', values)}
          startOpen
          filterState={filterState.numberSizes ?? []}
        />
      )}

      {isMobile() && mostly_pants && (
        <button
          type="button"
          onClick={() => setActivePage('pantSizes')}
          className="flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
        >
          <span className="text-[1.8rem] font-semibold text-black">
            Pant Size{' '}
          </span>

          <div className="flex items-center gap-2">
            {!!filterState.numberSizes?.length && (
              <span className="w-fit max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-400">
                {filterState.numberSizes.join(', ')}
              </span>
            )}

            <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
          </div>
        </button>
      )}
      {!isMobile() && mostly_pants && (
        <CheckboxFilterSection
          title="Pant Size"
          filterOptions={PANT_SIZES[
            (filterState.genders?.length === 1
              ? filterState.genders[0]
              : 'U') as Gender
          ].map((g) => ({ key: g + '', label: g + '' }))}
          onChange={(values) => handleFilterChange('numberSizes', values)}
          startOpen
          filterState={filterState.numberSizes ?? []}
        />
      )}
      {!mostly_parts && (
        <CheckboxFilterSection
          title="Gender / Age"
          filterOptions={['M', 'W', 'Y', 'U'].map((g) => ({
            value: g,
            key: g,
            label: getGenderLabel(g as Gender),
          }))}
          onChange={(values) => handleFilterChange('genders', values)}
          filterState={filterState.genders ?? []}
        />
      )}
      {mostly_tires && (
        <CheckboxFilterSection
          title="Width"
          filterOptions={TIRE_WIDTHS.map((item) => ({
            key: item.value,
            label: item.label,
          }))}
          onChange={(values) => handleFilterChange('widths', values)}
          filterState={filterState.widths ?? []}
          showSelectedFirst
        />
      )}
      {mostly_tires && (
        <CheckboxFilterSection
          title="Aspect Ratio"
          filterOptions={ASPECT_RATIOS.map((item) => ({
            key: item.value,
            label: item.label,
          }))}
          onChange={(values) => handleFilterChange('aspectRatios', values)}
          filterState={filterState.aspectRatios ?? []}
          showSelectedFirst
        />
      )}
      {mostly_tires && (
        <CheckboxFilterSection
          title="Rim Diameter"
          filterOptions={RIM_SIZES.map((item) => ({
            key: item.value,
            label: item.label,
          }))}
          onChange={(values) => handleFilterChange('rimDiameters', values)}
          filterState={filterState.rimDiameters ?? []}
          showSelectedFirst
        />
      )}
      {mostly_tires && (
        <CheckboxFilterSection
          title="Construction"
          filterOptions={CONSTRUCTIONS.map((item) => ({
            key: item.value,
            label: item.label,
          }))}
          onChange={(values) => handleFilterChange('constructions', values)}
          filterState={filterState.construction ?? []}
          showSelectedFirst
        />
      )}
      {mostly_tires && (
        <CheckboxFilterSection
          title="Mounting Position"
          filterOptions={MOUNTING_POSITIONS.map((item) => ({
            key: item.value,
            label: item.label,
          }))}
          onChange={(values) => handleFilterChange('mountingPositions', values)}
          filterState={filterState.mountingPositions ?? []}
          showSelectedFirst
        />
      )}

      {mostly_tires && (
        <CheckboxFilterSection
          title="Tire Diameter"
          filterOptions={facet_counts?.['specs.Tire Diameter']
            ?.sort((a, b) => +b.value - +a.value)
            .map((item) => ({
              key: item.value,
              label: item.value,
              count: item.count,
            }))}
          onChange={(values) => handleFilterChange('tireDiameters', values)}
          filterState={filterState.tireDiameters ?? []}
          facet_counts={facet_counts?.['specs.Tire Diameter']}
          showSelectedFirst
        />
      )}
      {isMobile() && (
        <>
          <button
            type="button"
            onClick={() => setActivePage('price')}
            className="flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
          >
            <span className="text-[1.8rem] font-semibold text-black">
              Price
            </span>

            <div className="flex items-center gap-2">
              {(filterState.minPrice || filterState.maxPrice) && (
                <span className="w-fit max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-400">
                  {filterState.minPrice && filterState.maxPrice
                    ? `$${filterState.minPrice} - $${filterState.maxPrice}`
                    : filterState.minPrice
                    ? `Over $${filterState.minPrice}`
                    : `Up to $${filterState.maxPrice}`}
                </span>
              )}

              <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActivePage('shipsTo')}
            className="flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
          >
            <span className="text-[1.8rem] font-semibold text-black">
              Ships To
            </span>

            <div className="flex items-center gap-2">
              {!!filterState.shipTo?.length && (
                <span className="w-fit max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-400">
                  {filterState.shipTo.map(getCountryLabelFromCode).join(', ')}
                </span>
              )}

              <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActivePage('shipsFrom')}
            className="flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
          >
            <span className="text-[1.8rem] font-semibold text-black">
              Ships From
            </span>

            <div className="flex items-center gap-2">
              {!!filterState.shipFrom?.length && (
                <span className="w-fit max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-400">
                  {filterState.shipFrom.map(getCountryLabelFromCode).join(', ')}
                </span>
              )}

              <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
            </div>
          </button>
        </>
      )}

      {!isMobile() && (!!brands?.length || !!brandFilter.length) && (
        <CheckboxFilterSection
          title="Brands"
          filterOptions={brands
            .filter((b) => b.label !== 'OEM')
            .map((b) => ({
              key: b.key,
              label: b.label,
              id: b.key,
            }))}
          showSelectedFirst
          facet_counts={facet_counts?.brand}
          onChange={(values) => {
            handleFilterChange('brands', values);
          }}
          filterState={filterState.brands ?? []}
          startOpen={true}
          noFiltersMessage="No brands found"
          noFiltersClassName="py-8"
        >
          <div className="mb-4 px-4 pb-4">
            <Input
              placeholder="Search All Brands"
              type="search"
              iconClassName="!h-6 !w-6"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="!h-[3.8rem] !rounded-[1.5rem]"
            />
          </div>
        </CheckboxFilterSection>
      )}

      {!isMobile() && (
        <>
          <PriceRangeFilterSection
            min={filterState.minPrice}
            max={filterState.maxPrice}
            title="Price"
            titleClassName="text-[1.8rem]"
            onChange={(min, max) => {
              handlePriceRangeChange(min, max);
            }}
            startOpen={!!filterState.minPrice || !!filterState.maxPrice}
          />

          <CheckboxFilterSection
            title="Ships To"
            filterOptions={countryOptions.map((code) => ({
              key: code,
              value: code,
              label: getCountryLabelFromCode(code),
            }))}
            onChange={(values) => {
              handleFilterChange('shipTo', values);
            }}
            filterState={filterState.shipTo ?? []}
          />
          <CheckboxFilterSection
            title="Ships From"
            filterOptions={countryOptions.map((code) => ({
              key: code,
              value: code,
              label: getCountryLabelFromCode(code),
            }))}
            onChange={(values) => {
              handleFilterChange('shipFrom', values);
            }}
            filterState={filterState.shipFrom ?? []}
          />
        </>
      )}
      {!isMobile() && (
        <Accordion startOpen title="Show Only" titleClassName="text-[1.8rem]">
          <div className="flex flex-col gap-y-4 pb-[1rem] pl-[1rem]">
            <Checkbox
              label="On Sale"
              onChange={() => {
                handleFilterChange('onsale', !filterState.onsale);
              }}
              selected={!!filterState.onsale}
            />
            <Checkbox
              label="Sold Items"
              onChange={() => {
                handleFilterChange('recentlySold', !filterState.recentlySold);
              }}
              selected={!!filterState.recentlySold}
            />
          </div>
        </Accordion>
      )}

      {isMobile() && (
        <CheckboxFilterSection
          title="Show Only"
          filterOptions={[
            { key: 'onsale', label: 'On Sale' },
            { key: 'recentlySold', label: 'Sold Items' },
          ]}
          onChange={(values) => {
            handleFilterChange('onsale', values.includes('onsale'));
            handleFilterChange('recentlySold', values.includes('recentlySold'));
          }}
          filterState={[
            ...(filterState.onsale ? ['onsale'] : []),
            ...(filterState.recentlySold ? ['recentlySold'] : []),
          ]}
          startOpen
        />
      )}
    </div>
  );
};

function CategoryLink({ cat }: { cat: CategoryDocument }) {
  const params = useSearchParams();
  const queryParams = Object.fromEntries(params?.entries() ?? []);
  delete queryParams.page;

  return (
    <Link
      href={{
        pathname: cat.full_slug,
        query: queryParams,
      }}
      key={cat.id}
      className="text-left text-[1.6rem] font-medium transition-all duration-150 ease-linear hover:font-semibold hover:text-brand-secondary"
    >
      {cat.name}
    </Link>
  );
}

function FacetCategoryLink({
  cat,
}: {
  cat: {
    value: string;
    count: number;
    order: string;
  };
}) {
  const params = useSearchParams();
  const pathname = usePathname();
  const queryParams = Object.fromEntries(params?.entries() ?? []);
  delete queryParams.page;

  return (
    <Link
      href={{
        pathname: pathname,
        query: {
          ...queryParams,
          [cat.order]: cat.value,
        },
      }}
      key={cat.value}
      className="text-left text-[1.6rem] font-medium transition-all duration-150 ease-linear hover:font-semibold hover:text-brand-secondary"
    >
      {cat.value} ({cat.count})
    </Link>
  );
}

export default Filters;
